/**
 * Sample places for iterating on the page's shape before (and alongside) the
 * real reads. Typed as the query result, so they cannot drift from the
 * selection; `fixtures.test.ts` also checks the key sets at runtime.
 *
 * Every number and name here is made up. The page labels fixture data "sample" wherever
 * it shows — see `isFixtureMode`.
 */
import type { PlaceData } from "@/data/places-api";
import type { EventResultData, PlaceSeasonsData, SeasonEventPage } from "@/data/place-events-api";
import type { SeasonFollowersData } from "@/data/place-members-api";
import type { SeasonStandingsData } from "@/data/place-standings-api";
import type { GameCatalogData, GameData, PlaceGamesData } from "@/data/place-games-api";
import { FIXTURES_ENABLED } from "@/config";
import { FACES_SHOWN } from "@/features/places/place-today";
import {
  EVENTS_PAGE,
  TOP_SHOWN,
  lastBegunEvents,
  placeEventsOf,
  seasonsToRead,
  type EventsApi,
  type PlaceEvents,
} from "@/features/places/last-events";
import { placeMembersOf, type MembersApi, type PlaceMembers, type SeasonFollowersRead } from "@/features/places/members";
import { placeStandingsOf, type PlaceStandings, type SeasonStandingsRead, type StandingsApi } from "@/features/places/standings";
import { placeGamesOf, type GamesApi, type PlaceGames } from "@/features/places/games";
import type { EventTemplateData, ScheduledEventData, UpcomingEventData } from "@/data/place-schedule-api";
import type { EventToChangeData } from "@/data/schedule-event-api";
import type { PresetChoice } from "@/data/presets-api";
import {
  draftContextOf,
  scheduleOptionsOf,
  type DraftContext,
  type ScheduleOptions,
  type ScheduleOptionsApi,
  type ScheduleWriteApi,
} from "@/features/places/schedule-event";
import {
  nextUpOf,
  placeScheduleOf,
  seasonsForUpcoming,
  soonestUpcoming,
  type NextUp,
  type PlaceSchedule,
  type ScheduleApi,
  type StaffScheduleApi,
} from "@/features/places/schedule";

type Room = NonNullable<PlaceData["rootGathering"]>;

/** Made-up names for sample faces (no photos: the rows draw initials). */
const SAMPLE_NAMES = [
  "Ada Brook", "Bo Chen", "Cleo Diaz", "Dev Patel", "Eli Ford", "Fay Gold", "Gus Hale",
  "Ivy James", "Jo Kent", "Kai Lund", "Lu Moss", "Max Nye", "Nia Owen", "Oz Park",
  "Pia Quinn", "Rex Stone", "Sol Tran", "Uma Vale", "Vic West", "Wren Young",
];

/** A room's people as `gatheringUsers(first: FACES_SHOWN)` returns them:
 *  `total` people, at most {@link FACES_SHOWN} of them listed. */
function people(prefix: string, total: number): Room["gatheringUsers"] {
  return {
    totalCount: total,
    nodes: Array.from({ length: Math.min(total, FACES_SHOWN) }, (_, i) => {
      const displayName = SAMPLE_NAMES[i % SAMPLE_NAMES.length]!;
      return {
        id: `${prefix}-u${i + 1}`,
        public: { displayName, username: displayName.toLowerCase().replace(" ", "_"), photoURL: "" },
      };
    }),
  };
}

const HOUSE_RULES = { id: "sample-preset-house", name: "House rules", slug: "house-rules" };
const OPEN_TABLE = { id: "sample-preset-open", name: "Open table", slug: "open-table" };

/** Every sample main room reads no visitor count, as every open main room
 *  does live: the backend counts a room's visitors only when it closes (see
 *  `allTimeLine` in `place-today.tsx`). Ben & Jerry's is the place with
 *  zeros: no one here, no table open, no event. */
export const PLACE_FIXTURES: PlaceData[] = [
  {
    id: "sample-parlor",
    name: "The Parlor",
    isDefault: true,
    brand: { id: "sample-brand-parlor", name: "The Parlor" },
    rootGathering: {
      id: "sample-parlor-main",
      title: "The Parlor",
      onlineUsersCount: 14,
      totalVisitorCount: null,
      activity: { id: "sample-game-liars-dice", slug: "liars-dice", name: "Liar's Dice" },
      gatheringUsers: people("sample-parlor-main", 14),
      settings: HOUSE_RULES,
    },
    subGatherings: {
      totalCount: 3,
      // Newest first, as the backend sends them; the page sorts by headcount.
      nodes: [
        {
          id: "sample-parlor-t3",
          title: "Late table",
          onlineUsersCount: 0,
          activity: { id: "sample-game-liars-dice", slug: "liars-dice", name: "Liar's Dice" },
          gatheringUsers: people("sample-parlor-t3", 0),
          settings: OPEN_TABLE,
        },
        {
          id: "sample-parlor-t2",
          title: "Window table",
          onlineUsersCount: 2,
          activity: null,
          gatheringUsers: people("sample-parlor-t2", 2),
          settings: null,
        },
        {
          id: "sample-parlor-t1",
          title: "Back booth",
          onlineUsersCount: 4,
          activity: { id: "sample-game-trivia", slug: "trivia", name: "Trivia" },
          gatheringUsers: people("sample-parlor-t1", 4),
          settings: OPEN_TABLE,
        },
      ],
    },
  },
  {
    id: "sample-parlor-garden",
    name: "The Parlor Garden",
    isDefault: false,
    brand: { id: "sample-brand-parlor", name: "The Parlor" },
    rootGathering: {
      id: "sample-garden-main",
      title: "Garden",
      onlineUsersCount: 3,
      totalVisitorCount: null,
      activity: { id: "sample-game-trivia", slug: "trivia", name: "Trivia" },
      gatheringUsers: people("sample-garden-main", 3),
      settings: HOUSE_RULES,
    },
    subGatherings: {
      totalCount: 1,
      nodes: [
        {
          id: "sample-garden-t1",
          title: "Patio",
          onlineUsersCount: 0,
          activity: null,
          gatheringUsers: people("sample-garden-t1", 0),
          settings: OPEN_TABLE,
        },
      ],
    },
  },
  {
    id: "sample-ben-and-jerrys",
    name: "Ben & Jerry's",
    isDefault: true,
    brand: { id: "sample-brand-ben-and-jerrys", name: "Ben & Jerry's" },
    rootGathering: {
      id: "sample-bj-main",
      title: "Scoop Shop",
      onlineUsersCount: 0,
      totalVisitorCount: null,
      activity: null,
      gatheringUsers: people("sample-bj-main", 0),
      settings: null,
    },
    subGatherings: { totalCount: 0, nodes: [] },
  },
];

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
/** Sample times count back from when the page loaded, so "the last 30 days"
 *  always has something in it. */
const LOADED_AT = Date.now();

type Listed = SeasonEventPage["nodes"][number];

/** A listed event starting `ago` ms before the page loaded, ending 3 hours
 *  after it starts. */
function listed(id: string, title: string, ago: number, stateUIHint: Listed["stateUIHint"]): Listed {
  return { id, title, startAt: LOADED_AT - ago, endAt: LOADED_AT - ago + 3 * HOUR, stateUIHint };
}

/** An event's results as the per-event read returns them: `came` visitors
 *  (null until it ends), `tables` tables opened, and a leaderboard whose first
 *  {@link TOP_SHOWN} players start at `SAMPLE_NAMES[from]` (null: no leaderboard;
 *  `players` 0: a leaderboard nobody scored on). */
function result(id: string, came: number | null, tables: number, players: number | null, from = 0): EventResultData {
  return {
    id,
    totalVisitorCount: came,
    gatherings: Array.from({ length: tables }, (_, i) => ({ id: `${id}-t${i + 1}` })),
    leaderboard:
      players === null
        ? null
        : {
            rankings: Array.from({ length: Math.min(players, TOP_SHOWN) }, (_, i) => {
              const displayName = SAMPLE_NAMES[(from + i) % SAMPLE_NAMES.length]!;
              return {
                user: {
                  id: `${id}-u${i + 1}`,
                  public: { displayName, username: displayName.toLowerCase().replace(" ", "_"), photoURL: "" },
                },
              };
            }),
          },
  };
}

/** A place's sample events, as the three reads return them: its seasons, one
 *  page of events per season (by season id), and each event's results (by id). */
export interface PlaceEventFixture {
  seasons: PlaceSeasonsData;
  pages: Record<string, SeasonEventPage>;
  results: Record<string, EventResultData>;
}

const NO_SEASONS = (id: string): PlaceEventFixture => ({
  seasons: { id, seasons: { totalCount: 0, nodes: [] } },
  pages: {},
  results: {},
});

/** The Parlor's five sample events across its two running seasons (interleaved
 *  in time), plus one still to come, which the section leaves out, and a season
 *  that has not begun, which is not read (its one event, still to come, is
 *  Next up's second). Its other place and Ben & Jerry's have no season. */
export const PLACE_EVENT_FIXTURES: Record<string, PlaceEventFixture> = {
  "sample-parlor": {
    seasons: {
      id: "sample-parlor",
      seasons: {
        totalCount: 3,
        // Newest first by start, as `Place.seasons` returns them.
        nodes: [
          { id: "sample-parlor-s2", title: "Winter Series", startAt: LOADED_AT + 20 * DAY, endAt: LOADED_AT + 80 * DAY },
          { id: "sample-parlor-s1", title: "Autumn League", startAt: LOADED_AT - 40 * DAY, endAt: LOADED_AT + 50 * DAY },
          { id: "sample-parlor-s0", title: "Summer Cup", startAt: LOADED_AT - 90 * DAY, endAt: LOADED_AT - 1 * DAY },
        ],
      },
    },
    pages: {
      // Not begun, so how the last ones went never reads it; Next up does.
      "sample-parlor-s2": {
        totalCount: 1,
        nodes: [listed("sample-ev-winter-opener", "Winter Series opener", -20 * DAY - 3 * HOUR, "waiting_to_start")],
      },
      "sample-parlor-s1": {
        totalCount: 4,
        // Newest first by start time, as `Season.events` returns them.
        nodes: [
          listed("sample-ev-finals", "Friday Finals", -2 * DAY, "waiting_to_start"),
          listed("sample-ev-trivia", "Trivia Night", 1 * HOUR, "started"),
          listed("sample-ev-dice", "Liar's Dice Cup", 6 * DAY, "finished"),
          listed("sample-ev-opening", "Opening Night", 34 * DAY, "finished"),
        ],
      },
      "sample-parlor-s0": {
        totalCount: 2,
        nodes: [
          listed("sample-ev-final", "Summer Cup final", 2 * DAY, "finished"),
          listed("sample-ev-semis", "Summer Cup semis", 13 * DAY, "finished"),
        ],
      },
    },
    results: {
      "sample-ev-finals": result("sample-ev-finals", null, 0, 0),
      "sample-ev-winter-opener": result("sample-ev-winter-opener", null, 0, 0),
      "sample-ev-trivia": result("sample-ev-trivia", null, 4, 9, 0),
      "sample-ev-final": result("sample-ev-final", 64, 11, 40, 3),
      "sample-ev-dice": result("sample-ev-dice", 38, 6, 0),
      "sample-ev-semis": result("sample-ev-semis", 21, 3, null),
      "sample-ev-opening": result("sample-ev-opening", 52, 9, 30, 7),
    },
  },
  "sample-parlor-garden": NO_SEASONS("sample-parlor-garden"),
  "sample-ben-and-jerrys": NO_SEASONS("sample-ben-and-jerrys"),
};

/** The three reads served from a fixture, a page at a time (for tests and
 *  proofs of the paging). */
export function fixtureEventsApi(fixture: PlaceEventFixture): EventsApi {
  return {
    seasons: async () => fixture.seasons,
    seasonEvents: async (seasonId, offset) => {
      const page = fixture.pages[seasonId];
      return page ? { totalCount: page.totalCount, nodes: page.nodes.slice(offset, offset + EVENTS_PAGE) } : null;
    },
    results: async (eventIds) => eventIds.map((id) => fixture.results[id] ?? null),
  };
}

/** A sample place's events as the page draws them; null for an unknown id.
 *  Synchronous (the samples fit one page per season), picking exactly as
 *  `readPlaceEvents` does. */
export function sampleEventsOf(placeId: string): PlaceEvents | null {
  const fixture = PLACE_EVENT_FIXTURES[placeId];
  if (!fixture) return null;
  const reads = seasonsToRead(fixture.seasons.seasons.nodes, LOADED_AT).map((season) => {
    const page = fixture.pages[season.id]!;
    return { season, events: page.nodes, total: page.totalCount };
  });
  const shown = lastBegunEvents(reads, LOADED_AT);
  // Every sample event has results, so who is reading makes no difference.
  return placeEventsOf(fixture.seasons, reads, shown, (id) => fixture.results[id] ?? null, LOADED_AT, { isSuperAdmin: false });
}

/** Sample person `n`, as a season's followers and standings name them: the
 *  same id in every season and in both. */
function member(n: number): SeasonFollowersData["nodes"][number] {
  const displayName = SAMPLE_NAMES[n % SAMPLE_NAMES.length]!;
  return { id: `sample-member-${n + 1}`, public: { displayName, username: displayName.toLowerCase().replace(" ", "_"), photoURL: "" } };
}

/** A season's followers as `Season.followers` returns them: the sample people
 *  `from` to `to - 1` (each the same person, by id, in every season). */
function followers(from: number, to: number): SeasonFollowersData {
  const nodes = Array.from({ length: to - from }, (_, i) => member(from + i));
  return { totalCount: nodes.length, nodes };
}

/** Each sample place's followers, by season id. The Parlor's two seasons that
 *  have begun share five people (15 and 8 followers, 18 people); its season
 *  still to come has three more, two of them new, and is not read. Its other
 *  place and Ben & Jerry's have no season, so no members. */
export const PLACE_MEMBER_FIXTURES: Record<string, Record<string, SeasonFollowersData>> = {
  "sample-parlor": {
    "sample-parlor-s2": followers(17, 20),
    "sample-parlor-s1": followers(0, 15),
    "sample-parlor-s0": followers(10, 18),
  },
  "sample-parlor-garden": {},
  "sample-ben-and-jerrys": {},
};

/** The members reads served from the samples (for tests and proofs). */
export function fixtureMembersApi(placeId: string): MembersApi {
  return {
    followerCount: async (seasonId) => PLACE_MEMBER_FIXTURES[placeId]?.[seasonId]?.totalCount ?? null,
    followers: async (seasonId) => PLACE_MEMBER_FIXTURES[placeId]?.[seasonId] ?? null,
  };
}

/** A sample place's seasons and each season's count, as the page's first
 *  members read returns them (the lists not read); null for an unknown id.
 *  Synchronous, picking the seasons exactly as `readMemberCounts` does. */
export function sampleMemberReadsOf(placeId: string): { seasons: PlaceSeasonsData; reads: SeasonFollowersRead[] } | null {
  const fixture = PLACE_EVENT_FIXTURES[placeId];
  if (!fixture) return null;
  const reads = seasonsToRead(fixture.seasons.seasons.nodes, LOADED_AT).map((season) => ({
    season,
    count: PLACE_MEMBER_FIXTURES[placeId]?.[season.id]?.totalCount ?? null,
  }));
  return { seasons: fixture.seasons, reads };
}

/** A sample place's members as the page draws them; null for an unknown id.
 *  The counts only, or (`lists`) with every season's list read, as after the
 *  partner opens the members. */
export function sampleMembersOf(placeId: string, { lists = false }: { lists?: boolean } = {}): PlaceMembers | null {
  const sample = sampleMemberReadsOf(placeId);
  if (!sample) return null;
  const reads = lists
    ? sample.reads.map((r) => ({ ...r, list: PLACE_MEMBER_FIXTURES[placeId]?.[r.season.id] ?? null }))
    : sample.reads;
  return placeMembersOf(sample.seasons, reads);
}

/** A season's best as `Leaderboard.rankings(first: 11)` returns them: the
 *  sample people (by index, the same ids as the members) with their points,
 *  best first, ranked 1, 2, 3… as the backend ranks (ties included). */
function rankings(entries: readonly [person: number, score: number][]): SeasonStandingsData {
  return entries.map(([n, score], i) => ({ rank: i + 1, score, user: member(n) }));
}

/** Each sample place's standings, by season id. The Parlor's two seasons that
 *  have begun rank the same people as its members (two of them in both):
 *  Autumn League, running, has more scorers than the top ten (the read's
 *  eleventh says so) and a tie; Summer Cup, ended, has six. Winter Series has
 *  not begun (no scores, not read). Its other place and Ben & Jerry's have no
 *  season, so no standings. */
export const PLACE_STANDINGS_FIXTURES: Record<string, Record<string, SeasonStandingsData>> = {
  "sample-parlor": {
    "sample-parlor-s2": rankings([]),
    "sample-parlor-s1": rankings([
      [11, 1840], [3, 1625], [12, 1410], [0, 1395], [7, 1200], [14, 980],
      [5, 760], [1, 760], [13, 540], [9, 310], [2, 120],
    ]),
    "sample-parlor-s0": rankings([[13, 2210], [10, 1980], [16, 1450], [11, 900], [17, 640], [15, 75]]),
  },
  "sample-parlor-garden": {},
  "sample-ben-and-jerrys": {},
};

/** The standings read served from the samples (for tests and proofs). */
export function fixtureStandingsApi(placeId: string): StandingsApi {
  return { standings: async (seasonId) => PLACE_STANDINGS_FIXTURES[placeId]?.[seasonId] ?? null };
}

/** A sample place's seasons and the first season's standings, as the page's
 *  first standings read returns them (the other tables not read); null for an
 *  unknown id, with the time it was "read". Synchronous, picking the seasons
 *  exactly as `readFirstStandings` does. */
export function sampleStandingsReadsOf(placeId: string): { seasons: PlaceSeasonsData; reads: SeasonStandingsRead[]; now: number } | null {
  const fixture = PLACE_EVENT_FIXTURES[placeId];
  if (!fixture) return null;
  const reads = seasonsToRead(fixture.seasons.seasons.nodes, LOADED_AT).map((season, i) =>
    i === 0 ? { season, rankings: PLACE_STANDINGS_FIXTURES[placeId]?.[season.id] ?? null } : { season },
  );
  return { seasons: fixture.seasons, reads, now: LOADED_AT };
}

/** A sample place's standings as the page draws them; null for an unknown id.
 *  The first season's table only, or (`tables`) every season's, as after the
 *  partner opens the standings. */
export function sampleStandingsOf(placeId: string, { tables = false }: { tables?: boolean } = {}): PlaceStandings | null {
  const sample = sampleStandingsReadsOf(placeId);
  if (!sample) return null;
  const reads = tables
    ? sample.reads.map((r) => ({ season: r.season, rankings: PLACE_STANDINGS_FIXTURES[placeId]?.[r.season.id] ?? null }))
    : sample.reads;
  return placeStandingsOf(sample.seasons, reads, sample.now);
}

/** A sample catalog game. */
function game(slug: string, name: string, activityServerType: string, isEnabled = true): GameData {
  return { id: `sample-game-${slug}`, name, slug, activityServerType, isEnabled };
}

const LIARS_DICE = game("liars-dice", "Liar's Dice", "internal-server");
const TRIVIA = game("trivia", "Trivia", "cloudflare-worker");
/** The platform defaults a place falls back to when it pins none: the
 *  lobby for a main room (no game server, as on dev), pool for tables. */
const LOBBY = game("lobby", "Lobby", "no-server");
const POOL = game("pool", "Pool", "internal-server");

/** The sample catalog, as staff read it (`all: true`): in the catalog's own
 *  order, with one game switched off. */
export const GAME_CATALOG_FIXTURE: GameCatalogData = {
  all: true,
  nodes: [
    LOBBY,
    LIARS_DICE,
    TRIVIA,
    game("darts", "Darts", "cloudflare-worker"),
    POOL,
    game("snakes-and-ladders", "Snakes & Ladders", "cloudflare-worker"),
    game("bingo", "Bingo", "internal-server", false),
  ],
};

/** A place's game settings: its brand's list (`null`: a house place), and
 *  what its main room and tables start on (`null`: the platform default). */
function placeGames(id: string, slugs: string[] | null, brandId: string, main: GameData | null, tables: GameData | null): PlaceGamesData {
  return {
    id,
    brand: slugs ? { id: brandId, allowedGameSlugs: slugs } : null,
    defaultActivityForRootGathering: main ? { id: main.id } : null,
    defaultActivityForSubGatherings: tables ? { id: tables.id } : null,
    resolvedRootGatheringDefaults: { activity: main ?? LOBBY },
    resolvedSubGatheringDefaults: { activity: tables ?? POOL },
  };
}

/** The Parlor's list: five games, one of them switched off ("bingo") and one
 *  that names no game in the catalog ("shuffleboard"). */
export const PARLOR_GAMES = ["liars-dice", "trivia", "darts", "bingo", "shuffleboard"];

/** Each sample place's game settings. The Parlor pins both defaults (Liar's
 *  Dice in the main room, Trivia at the tables); its garden shares the brand's
 *  list and pins the main room only; Ben & Jerry's lists no games and pins
 *  neither, so it starts on the platform defaults. */
export const PLACE_GAMES_FIXTURES: Record<string, PlaceGamesData> = {
  "sample-parlor": placeGames("sample-parlor", PARLOR_GAMES, "sample-brand-parlor", LIARS_DICE, TRIVIA),
  "sample-parlor-garden": placeGames("sample-parlor-garden", PARLOR_GAMES, "sample-brand-parlor", TRIVIA, null),
  "sample-ben-and-jerrys": placeGames("sample-ben-and-jerrys", [], "sample-brand-ben-and-jerrys", null, null),
};

/** The sample catalog as `isSuperAdmin` reads it: staff, every game (as
 *  {@link GAME_CATALOG_FIXTURE}); anyone else, the enabled games only. */
export function sampleCatalogFor(isSuperAdmin: boolean): GameCatalogData {
  if (isSuperAdmin) return GAME_CATALOG_FIXTURE;
  return { all: false, nodes: GAME_CATALOG_FIXTURE.nodes.filter((g) => g.isEnabled) };
}

/** The games reads served from the samples (for tests and proofs), as
 *  `isSuperAdmin` reads them. */
export function fixtureGamesApi(isSuperAdmin: boolean): GamesApi {
  return {
    place: async (placeId) => PLACE_GAMES_FIXTURES[placeId] ?? null,
    catalog: async () => sampleCatalogFor(isSuperAdmin),
  };
}

/** A sample place's games as the page draws them for `isSuperAdmin`; null
 *  for an unknown id. Synchronous, joining exactly as `readPlaceGames` does,
 *  so a partner's sample page says the partner's words. */
export function sampleGamesOf(placeId: string, isSuperAdmin: boolean): PlaceGames | null {
  const place = PLACE_GAMES_FIXTURES[placeId];
  return place ? placeGamesOf(place, sampleCatalogFor(isSuperAdmin)) : null;
}

/** The game and rules preset of each sample event still to come, by event id
 *  (as the details read returns them). */
export const UPCOMING_DETAIL_FIXTURES: Record<string, UpcomingEventData> = {
  "sample-ev-finals": { id: "sample-ev-finals", gatheringActivity: { name: "Liar's Dice" }, gatheringSettings: { name: "House rules" } },
  "sample-ev-winter-opener": { id: "sample-ev-winter-opener", gatheringActivity: { name: "Hearts" }, gatheringSettings: { name: "Open table" } },
};

const MINUTE = 60 * 1000;

/** The sample templates, as the templates read returns them: every place's,
 *  not one's. One names The Parlor (`placeId`), one a season of The Parlor's
 *  (`seasonId`), one neither. The first has its own run length; the second
 *  the backend's default. */
export const TEMPLATE_FIXTURES: EventTemplateData[] = [
  {
    id: "sample-tpl-hearts",
    name: "Hearts night",
    defaultDuration: 90 * MINUTE,
    templateJson: JSON.stringify({
      title: "Hearts night",
      placeId: "sample-parlor",
      seasonId: null,
      gatheringActivitySlug: "activity_hearts",
      gatheringSettingsTagSlug: "house-rules",
      lobbyGatheringSettingsTagSlug: "open-table",
      startAt: 0,
      beginWindDownAt: 90 * MINUTE,
    }),
  },
  {
    id: "sample-tpl-league",
    name: "Autumn League night",
    // The backend's default (never set, or set so): shown as such.
    defaultDuration: 45 * MINUTE,
    templateJson: JSON.stringify({
      title: "League night",
      placeId: null,
      seasonId: "sample-parlor-s1",
      gatheringActivitySlug: "activity_liars_dice",
      gatheringSettingsTagSlug: "open-table",
      startAt: 0,
      beginWindDownAt: 2 * HOUR,
    }),
  },
  {
    id: "sample-tpl-popup",
    name: "Pop-up trivia",
    defaultDuration: 45 * MINUTE,
    templateJson: JSON.stringify({
      title: "Pop-up trivia",
      placeId: null,
      seasonId: null,
      gatheringActivitySlug: "activity_trivia",
      startAt: 0,
      beginWindDownAt: 45 * MINUTE,
    }),
  },
];

/** The sample repeats, as the repeats read returns them: one, Hearts at The
 *  Parlor on Tuesdays and Thursdays at 7:00 PM Los Angeles time for 90
 *  minutes, made from the template that names The Parlor. */
export const REPEAT_FIXTURES: ScheduledEventData[] = [
  {
    id: "sample-repeat-hearts",
    name: "Hearts at the Parlor",
    startEventOnDays: ["Thu", "Tue"],
    eventStartTimeHour: 19,
    eventStartTimeMinute: 0,
    timeZone: "America/Los_Angeles",
    eventDurationInMs: 90 * MINUTE,
    nextEventToBeCreatedAt: LOADED_AT + 1 * DAY + 5 * HOUR,
    eventTemplate: { id: "sample-tpl-hearts", name: "Hearts night", templateJson: TEMPLATE_FIXTURES[0]!.templateJson },
  },
];

/** Next up's reads served from the samples (for tests and proofs). */
export function fixtureScheduleApi(placeId: string): ScheduleApi {
  const events = fixtureEventsApi(PLACE_EVENT_FIXTURES[placeId] ?? NO_SEASONS(placeId));
  return {
    seasonEvents: events.seasonEvents,
    details: async (eventIds) => eventIds.map((id) => UPCOMING_DETAIL_FIXTURES[id] ?? null),
  };
}

/** The two staff reads served from the samples (for tests and proofs). */
export function fixtureStaffScheduleApi(): StaffScheduleApi {
  return { templates: async () => TEMPLATE_FIXTURES, repeats: async () => REPEAT_FIXTURES };
}

/** A sample place's seasons and Next up, as the page's first schedule read
 *  returns them; null for an unknown id. Synchronous (the samples fit one page
 *  per season), picking exactly as `readNextUp` does. */
export function sampleNextUpOf(placeId: string): { seasons: PlaceSeasonsData; nextUp: NextUp } | null {
  const fixture = PLACE_EVENT_FIXTURES[placeId];
  if (!fixture) return null;
  const reads = seasonsForUpcoming(fixture.seasons.seasons.nodes, LOADED_AT).map((season) => {
    const page = fixture.pages[season.id]!;
    return { season, events: page.nodes, total: page.totalCount };
  });
  const shown = soonestUpcoming(reads, LOADED_AT);
  // Every sample event still to come has details, so who is reading makes no difference.
  const nextUp = nextUpOf(fixture.seasons, reads, shown, (id) => UPCOMING_DETAIL_FIXTURES[id] ?? null, LOADED_AT, { isSuperAdmin: false });
  return { seasons: fixture.seasons, nextUp };
}

/** A sample place's schedule as the page draws it for `viewer`; null for an
 *  unknown id. Staff groups unread, or (`opened`) read from the samples, as
 *  after staff open the section. */
export function sampleScheduleOf(
  placeId: string,
  { viewer, opened = false }: { viewer: { isSuperAdmin: boolean }; opened?: boolean },
): PlaceSchedule | null {
  const sample = sampleNextUpOf(placeId);
  if (!sample) return null;
  const staff = opened ? { templates: TEMPLATE_FIXTURES, repeats: REPEAT_FIXTURES } : "unread";
  return placeScheduleOf(placeId, sample.seasons, sample.nextUp, staff, viewer);
}

/** The sample rules presets, as the presets read returns them: the rooms'
 *  two above, the admin console's two for events, and one with no slug
 *  (which the form cannot offer). */
export const PRESET_FIXTURES: PresetChoice[] = [
  HOUSE_RULES,
  OPEN_TABLE,
  { id: "sample-preset-event-lobby", name: "Event lobby", slug: "event-lobby" },
  { id: "sample-preset-event-tables", name: "Event tables", slug: "event-multi-room" },
  { id: "sample-preset-unslugged", name: "Unnamed preset", slug: null },
];

/** The form's two reads served from the samples (staff's catalog). */
export function fixtureScheduleOptionsApi(): ScheduleOptionsApi {
  return { catalog: async () => GAME_CATALOG_FIXTURE, presets: async () => PRESET_FIXTURES };
}

/** The form's games and presets from the samples, synchronously. */
export function sampleScheduleOptions(): ScheduleOptions {
  return scheduleOptionsOf(GAME_CATALOG_FIXTURE, PRESET_FIXTURES);
}

/** One of The Parlor's coming events as the read by id returns it: its
 *  start and end from its Next up row, the rest made up; `over` sets the rest. */
function eventToChange(id: string, over: Partial<EventToChangeData>): EventToChangeData {
  const row = Object.values(PLACE_EVENT_FIXTURES["sample-parlor"]!.pages).flatMap((p) => p.nodes).find((e) => e.id === id)!;
  return {
    id,
    title: row.title,
    description: "",
    stateUIHint: row.stateUIHint,
    startAt: row.startAt,
    revealAt: LOADED_AT - DAY,
    beginWindDownAt: row.startAt + 60 * MINUTE,
    endAt: row.endAt,
    visibility: "PUBLIC",
    featured: false,
    gatheringCount: 1,
    showFinalResultsDuration: 60 * MINUTE,
    hasCountdownState: false,
    countdownTime: null,
    matchmakingEnabled: false,
    matchmakingWaitingText: null,
    subgatheringViewMode: "card",
    deepLinkToSubgatheringsEnabled: true,
    howItWorksIntercomArticleId: null,
    tileBackgroundCloudPath: null,
    tileBackground: null,
    overrideMatchmakingPlayerCounts: null,
    overrideMatchmakingMinPlayerCount: null,
    overrideMatchmakingMaxPlayerCount: null,
    overrideMatchmakingWaitTimeForPartialMatchMs: null,
    overrideMatchmakingAbsoluteMaxWaitTimeMs: null,
    gatheringActivity: { slug: "liars-dice", name: "Liar's Dice" },
    gatheringSettings: { slug: HOUSE_RULES.slug, name: HOUSE_RULES.name },
    eventConfig: { lobbyGatheringSettingsTag: { slug: HOUSE_RULES.slug, name: HOUSE_RULES.name } },
    host: { public: { username: "aj" } },
    gatheringGameMode: null,
    flatPrizingConfiguration: null,
    season: { id: "sample-parlor-s1", title: "Autumn League" },
    ownPlace: { id: "sample-parlor" },
    eventTemplate: null,
    attachedProducts: [],
    guestList: { totalCount: 0, nodes: [] },
    leaderboard: { title: "Leaderboard", scoringMetrics: [] },
    followerCount: 0,
    ...over,
  };
}

/** The Parlor's two coming events as Change and Cancel read them, by id.
 *  Friday Finals: plain and public. The Winter Series opener: Hearts (not a
 *  catalog game here, so the form shows it as not offered), Open table,
 *  private with a guest list, a product attached and followers (what Cancel's
 *  undo says it cannot carry). Within a minute of a start: the proof sets its
 *  clock there (a sample that close would start while the page is open). */
export const EVENT_TO_CHANGE_FIXTURES: Record<string, EventToChangeData> = {
  "sample-ev-finals": eventToChange("sample-ev-finals", {}),
  "sample-ev-winter-opener": eventToChange("sample-ev-winter-opener", {
    gatheringActivity: { slug: "activity_hearts", name: "Hearts" },
    gatheringSettings: { slug: OPEN_TABLE.slug, name: OPEN_TABLE.name },
    eventConfig: { lobbyGatheringSettingsTag: { slug: OPEN_TABLE.slug, name: OPEN_TABLE.name } },
    season: { id: "sample-parlor-s2", title: "Winter Series" },
    visibility: "PRIVATE",
    guestList: {
      totalCount: 3,
      nodes: ["ada_brook", "bo_chen", "cleo_diaz"].map((username) => ({ public: { username } })),
    },
    attachedProducts: [{ product: { id: "sample-product-badge" } }],
    followerCount: 12,
  }),
};

/** The writes and the read by id, served without a network: the create
 *  answers with a sample event (nothing is made anywhere), the read with
 *  {@link EVENT_TO_CHANGE_FIXTURES}, the cancel and the change do nothing. */
export function fixtureScheduleWriteApi(): ScheduleWriteApi {
  return {
    create: async (input) => ({ id: "sample-made-event", title: input.title, startAt: input.startAt }),
    cancel: async () => {},
    event: async (eventId) => EVENT_TO_CHANGE_FIXTURES[eventId] ?? null,
    update: async () => {},
  };
}

/** A sample place's form defaults' context, as staff read it at `now`; null
 *  for an unknown id. The Parlor: Liar's Dice (its main room's pin), House
 *  rules (its main room's preset), the Autumn League (running now). */
export function sampleDraftContextOf(placeId: string, now: number): DraftContext | null {
  const place = PLACE_FIXTURES.find((p) => p.id === placeId);
  if (!place) return null;
  return draftContextOf({
    now,
    place,
    games: sampleGamesOf(placeId, true),
    seasons: PLACE_EVENT_FIXTURES[placeId]?.seasons ?? null,
    options: sampleScheduleOptions(),
  });
}

/**
 * Whether `?fixtures=1` may swap the live read for fixtures: the opt-in
 * `BUN_PUBLIC_ENABLE_FIXTURES` flag (see `FIXTURES_ENABLED` in `@/config`).
 * On in dev and the dev preview, off in prod, and off when the flag is absent.
 */
export const FIXTURES_ALLOWED = FIXTURES_ENABLED;

/** True when this location asks for fixtures and they are allowed here. */
export function isFixtureMode(search: string, allowed: boolean = FIXTURES_ALLOWED): boolean {
  return allowed && new URLSearchParams(search).get("fixtures") === "1";
}

/** The query string a link inside the places pages carries forward: only
 *  `?fixtures=1` when present, never any other parameter. */
export function fixtureQuery(search: string): string {
  return new URLSearchParams(search).get("fixtures") === "1" ? "?fixtures=1" : "";
}
